import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthProvider';
import { UserPrefsProvider, useUserPrefs } from './UserPrefsProvider';
import { UIProvider } from './UIContext';
import { SessionProvider } from './SessionProvider';
import { ActiveSessionProvider } from './ActiveSessionContext';
import Navigation from './Navigation';
import SessionBanner from './SessionBanner';
import AddTunePage from './pages/AddTunePage';
import SearchPage from './pages/SearchPage';
import SettingsPage from './pages/SettingsPage';
import TuneDetailPage from './pages/TuneDetailPage';
import SessionViewer from './pages/SessionViewer';
import Login from './Login';
import EditTunePage from './pages/EditTunePage';
import SetsPage from './pages/SetsPage';
import EditSetPage from './pages/EditSetPage';
import SetDetailPage from './pages/SetDetailPage';
import { flushPendingWrites } from './utils/flushPendingWrites';
import { useEffect } from 'react';
import EditSessionPage from './pages/EditSessionPage';


// 🧠 Moves useAuth() inside a child of AuthProvider
function AppRoutesWrapper() {
  const { user, loading } = useAuth();
  const { prefs } = useUserPrefs();

  useEffect(() => {
    if (!user) return;
    const handleOnline = () => flushPendingWrites(user);
    window.addEventListener('online', handleOnline);
    flushPendingWrites(user);
    return () => window.removeEventListener('online', handleOnline);
  }, [user]);

  if (loading || !prefs) return <div>Loading...</div>;
  if (!user) return <Login />;

  return (
    <UIProvider initialUiSize={prefs.uiSize}>
      <ActiveSessionProvider>
        <Router>
          <Navigation />
          <SessionBanner />
          <Routes>
            <Route path="/add" element={<AddTunePage />} />
            <Route path="/mytunes" element={<SearchPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/sessions" element={<SessionViewer />} />
            <Route path="/editsession/:sessionId" element={<EditSessionPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/tune/:id" element={<TuneDetailPage />} />
            <Route path="/edit/:id" element={<EditTunePage />} />
            <Route path="/tune/:id/edit" element={<EditTunePage />} />
            <Route path="/sets" element={<SetsPage />} />
            <Route path="/sets/:id" element={<SetDetailPage />} />
            <Route path="/sets/:id/edit" element={<EditSetPage />} />
            <Route path="*" element={<Navigate to="/search" replace />} />
          </Routes>
        </Router>
      </ActiveSessionProvider>
    </UIProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <UserPrefsProvider>
        <AppRoutesWrapper />
      </UserPrefsProvider>
    </AuthProvider>
  );
}
